import React, { useRef, useEffect } from 'react';
import { format, isToday } from 'date-fns';
import { cn } from '@/lib/utils';
import type { CalendarEvent } from '@/hooks/useCalendarEvents';
import type { AgendaItem } from '@/hooks/useAgendaEvents';

type DayViewEvent = CalendarEvent | AgendaItem;

const HOUR_HEIGHT = 64; // px per hour
const START_HOUR = 0;
const END_HOUR = 24;
const HOURS = Array.from({ length: END_HOUR - START_HOUR }, (_, i) => START_HOUR + i);

const TYPE_STYLES: Record<string, string> = {
  meeting: 'bg-blue-500 text-white border-blue-600',
  reminder: 'bg-amber-500 text-white border-amber-600',
  other: 'bg-purple-500 text-white border-purple-600',
  unavailable: 'bg-gray-400 text-white border-gray-500',
};

interface LayoutedEvent {
  event: DayViewEvent;
  col: number;
  maxCol: number;
  top: number;
  height: number;
}

function layoutEvents(events: DayViewEvent[]): {
  timedEvents: LayoutedEvent[];
  allDayEvents: DayViewEvent[];
} {
  const allDayEvents = events.filter((e) => e.all_day);
  const timedRaw = events.filter((e) => !e.all_day);

  const sorted = [...timedRaw].sort(
    (a, b) => new Date(a.start_at).getTime() - new Date(b.start_at).getTime(),
  );

  // Greedy column assignment
  const colEndTimes: number[] = [];
  const assignments: Array<{
    event: DayViewEvent;
    col: number;
    top: number;
    height: number;
  }> = [];

  for (const event of sorted) {
    const start = new Date(event.start_at);
    const end = new Date(event.end_at);
    const startMs = start.getTime();
    const endMs = Math.max(end.getTime(), startMs + 15 * 60_000); // min 15 min

    const startMins = (start.getHours() - START_HOUR) * 60 + start.getMinutes();
    const endMins = (new Date(endMs).getHours() - START_HOUR) * 60 + new Date(endMs).getMinutes();
    const top = (startMins / 60) * HOUR_HEIGHT;
    const height = Math.max(((endMins - startMins) / 60) * HOUR_HEIGHT, 22);

    let col = colEndTimes.findIndex((t) => t <= startMs);
    if (col === -1) {
      col = colEndTimes.length;
      colEndTimes.push(endMs);
    } else {
      colEndTimes[col] = endMs;
    }

    assignments.push({ event, col, top, height });
  }

  const maxCol = Math.max(colEndTimes.length, 1);

  return {
    timedEvents: assignments.map((a) => ({ ...a, maxCol })),
    allDayEvents,
  };
}

interface DayViewProps {
  date: Date;
  events: DayViewEvent[];
  onEventClick: (event: DayViewEvent) => void;
  onTimeSlotClick: (date: Date) => void;
}

export function DayView({ date, events, onEventClick, onTimeSlotClick }: DayViewProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const { timedEvents, allDayEvents } = layoutEvents(events);
  const todayFlag = isToday(date);

  // Scroll to current hour (or 8 am)
  useEffect(() => {
    if (scrollRef.current) {
      const targetHour = todayFlag ? Math.max(new Date().getHours() - 1, START_HOUR) : 8;
      scrollRef.current.scrollTop = Math.max(0, (targetHour - START_HOUR) * HOUR_HEIGHT - 32);
    }
  }, [date]);

  // Current time indicator
  const now = new Date();
  const nowMins = (now.getHours() - START_HOUR) * 60 + now.getMinutes();
  const nowTop = (nowMins / 60) * HOUR_HEIGHT;
  const showNowLine =
    todayFlag && now.getHours() >= START_HOUR && now.getHours() < END_HOUR;

  const handleGridClick = (e: React.MouseEvent<HTMLDivElement>) => {
    // Ignore clicks on event buttons
    if ((e.target as HTMLElement).closest('button')) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const y = e.clientY - rect.top;
    const minsFromStart = (y / HOUR_HEIGHT) * 60;
    const totalMins = START_HOUR * 60 + minsFromStart;
    const hours = Math.floor(totalMins / 60);
    const mins = Math.round((totalMins % 60) / 15) * 15;
    const clicked = new Date(date);
    clicked.setHours(Math.min(hours, END_HOUR - 1), Math.min(mins, 59), 0, 0);
    onTimeSlotClick(clicked);
  };

  return (
    <div className="flex flex-col border border-border rounded-lg overflow-hidden bg-background">
      {/* All-day strip */}
      {allDayEvents.length > 0 && (
        <div className="border-b border-border bg-muted/20 py-2">
          <div className="text-xs text-muted-foreground mb-1 pl-16">Dia inteiro</div>
          <div className="flex flex-col gap-1 pl-16 pr-2">
            {allDayEvents.map((ev) => {
              const isCanceled = (ev as any).status === 'canceled';
              const isCompleted = (ev as any).status === 'completed';
              return (
                <button
                  key={ev.id}
                  className={cn(
                    'text-left text-xs rounded px-2 py-0.5 truncate border',
                    TYPE_STYLES[ev.type] ?? TYPE_STYLES.other,
                    (isCanceled || isCompleted) && 'opacity-50 border-dashed',
                  )}
                  onClick={() => onEventClick(ev)}
                >
                  <span className={cn((isCanceled || isCompleted) && 'line-through')}>{ev.title}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Scrollable time grid */}
      <div ref={scrollRef} className="overflow-y-auto flex-1 min-h-0">
        <div
          className="relative cursor-pointer select-none"
          style={{ height: HOURS.length * HOUR_HEIGHT }}
          onClick={handleGridClick}
        >
          {/* Hour lines */}
          {HOURS.map((h) => (
            <div
              key={h}
              className="absolute w-full flex items-start pointer-events-none"
              style={{ top: (h - START_HOUR) * HOUR_HEIGHT }}
            >
              <span className="w-16 text-right pr-3 text-xs text-muted-foreground -mt-2 shrink-0">
                {`${String(h).padStart(2, '0')}:00`}
              </span>
              <div className="flex-1 border-t border-border" />
            </div>
          ))}

          {/* Half-hour lines */}
          {HOURS.map((h) => (
            <div
              key={`${h}h`}
              className="absolute w-full flex items-start pointer-events-none"
              style={{ top: (h - START_HOUR) * HOUR_HEIGHT + HOUR_HEIGHT / 2 }}
            >
              <span className="w-16 shrink-0" />
              <div className="flex-1 border-t border-border/30" />
            </div>
          ))}

          {/* Current time line */}
          {showNowLine && (
            <div
              className="absolute flex items-center z-10 pointer-events-none"
              style={{ top: nowTop, left: 0, right: 0 }}
            >
              <span className="w-16 flex justify-end pr-2 shrink-0">
                <span className="w-2 h-2 rounded-full bg-red-500" />
              </span>
              <div className="flex-1 border-t-2 border-red-500" />
            </div>
          )}

          {/* Events */}
          <div className="absolute top-0 bottom-0 left-16 right-2">
            {timedEvents.map(({ event, col, maxCol, top, height }) => {
              const GAP = 2;
              const colWidth = 100 / maxCol;
              const left = `calc(${col * colWidth}% + ${col > 0 ? GAP : 0}px)`;
              const width = `calc(${colWidth}% - ${GAP}px)`;
              const start = new Date(event.start_at);
              const end = new Date(event.end_at);

              const isCanceled = (event as any).status === 'canceled';
              const isCompleted = (event as any).status === 'completed';

              return (
                <button
                  key={event.id}
                  className={cn(
                    'absolute rounded px-1.5 py-0.5 text-left text-xs overflow-hidden border z-[1]',
                    'hover:brightness-110 transition-all',
                    TYPE_STYLES[event.type] ?? TYPE_STYLES.other,
                    (isCanceled || isCompleted) && 'opacity-50 border-dashed',
                  )}
                  style={{ top, height, left, width }}
                  onClick={(e) => {
                    e.stopPropagation();
                    onEventClick(event);
                  }}
                >
                  <div className={cn("font-medium truncate leading-4", (isCanceled || isCompleted) && "line-through")}>{event.title}</div>
                  {height > 30 && (
                    <div className="opacity-80 text-[10px]">
                      {format(start, 'HH:mm')} – {format(end, 'HH:mm')}
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
